import { useCallback, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Link from '@/components/atoms/Link/Link';
import { FormAutocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { updateContext } from '@/modules/tryIt/redux/actions';
import { selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';

import { useBundleSection } from '../../hooks/useBundleSection';
import { ContextSectionWrapper } from '../ContextSectionWrapper/ContextSectionWrapper';

export type ContextBundle = { bundle: string; desk: string; [key: string]: string } | null;

type Props = { canEdit: boolean; bundle: ContextBundle };

export const BundleSection = ({ canEdit, bundle }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const isReplay = useSelector(selectIsReplay);
  const { context, isBundleEmpty, bundles, selectedDeskId, selectedBundleId, autocompleteOptions } =
    useBundleSection(bundle);

  const { setValue, control } = useForm<{ contextBundle: string | null }>({
    mode: 'onChange',
    defaultValues: { contextBundle: null },
  });

  // Map options to FormAutocomplete format (using category instead of desk_name)
  const comboboxOptions = useMemo(
    () =>
      autocompleteOptions.map((option) => ({
        value: option.value,
        label: option.label,
        category: option.desk_name ?? undefined,
      })),
    [autocompleteOptions]
  );

  const resetBundle = useCallback(() => {
    setValue('contextBundle', null);
    const newContext = context[context.length - 1] ?? {};

    dispatch(updateContext([...context.slice(0, -1), { ...newContext, global: { bundle: null } }]));
  }, [context, dispatch, setValue]);

  const handleChange = useCallback(
    (value: string) => {
      const option = autocompleteOptions.find((opt) => opt.value === value);
      if (!option) {
        return;
      }

      const newContext = context[context.length - 1] ?? {};
      const bundleVariables = bundles.find((b) => b.name === option.label)?.context;

      dispatch(
        updateContext([
          ...context.slice(0, -1),
          {
            ...newContext,
            global: {
              bundle: { ...bundleVariables, bundle: option.label, desk: option.desk_name },
            },
          },
        ])
      );
    },
    [autocompleteOptions, bundles, context, dispatch]
  );

  if ((!canEdit || isReplay) && isBundleEmpty) {
    return null;
  }

  const hideResetIcon = !canEdit || isReplay || (isBundleEmpty && canEdit);

  return (
    <ContextSectionWrapper
      title={t('try_it.bundle')}
      onClick={resetBundle}
      hideIcon={hideResetIcon}
    >
      {isBundleEmpty && canEdit ? (
        <FormAutocomplete
          control={control}
          options={comboboxOptions}
          name="contextBundle"
          placeholder={t('try_it.select_bundle')}
          onChange={handleChange}
        />
      ) : (
        <div className="flex">
          <div className="mr-2 ml-2.5">
            <p className="mb-1.5 text-sm leading-5 font-normal text-foreground-muted">
              {t('common.desk')}:
            </p>

            <p className="text-sm leading-5 font-normal text-foreground-muted">
              {t('try_it.bundle')}:
            </p>
          </div>
          <div className="pl-1.5">
            <p className="mb-1.5 text-sm leading-5 font-normal text-informational">
              <Link href={`/${slug}/environments/${selectedDeskId}`}>{bundle?.desk}</Link>
            </p>

            <p className="text-sm leading-5 font-normal text-informational">
              <Link
                href={`/${slug}/environments/${selectedDeskId}/context_bundles/${selectedBundleId}`}
              >
                {bundle?.bundle}
              </Link>
            </p>
          </div>
        </div>
      )}
    </ContextSectionWrapper>
  );
};
